import {
  changeUserSettingsRequest,
  deleteUserRequest,
  editMyProfileRequest,
  fetchAllUsersRequest,
  fetchConversionalRolesRequest,
  fetchMeRequest,
  fetchUserBranches,
  fetchUserRequest,
  fetchUserSettingsRequest,
} from '@/services/api/user';
import {message} from 'antd';
import {processAPIResponse} from '@/utils/request';
import {BackofficeUserModel} from '@/typings/api/user';
import {trackModalView} from '@/services/tracker';
import {getIntlTranslation, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import {mergeAndSaveListParams} from '@/utils/listParams';
import {parseSort} from '@/utils/functions';
import mergeWith from 'lodash/mergeWith';

const initialState = {
  loggedIn: false,
  settings: {},
  list: [],
  listParams: {},
  conversionalRoles: [],
  transferableRoles: [],
  userBranches: [],
  agentDetails: {},
  drawer: {
    visible: false,
  },
  detailsDrawer: {},
};

export default {
  namespace: 'user',

  state: initialState,

  effects: {
    * loggedIn({ payload = {} }: { payload: BackofficeUserModel }, { put }) {
      const { User, Partner, Journeys, Role } = payload;
      yield put({
        type: 'gotMe',
        // TODO: Check Avatar
        payload: {
          user: { ...User, avatar: 'https://pbs.twimg.com/profile_images/1362000555820937217/fNKKSMBa_200x200.jpg' },
          partner: Partner,
          role: Role,
        },
      });
      yield put({
        type: 'journey/gotJourneys',
        payload: Journeys,
      });
      // yield put({
      //   type: 'fetchSettings'
      // })
    },
    * fetchSettings({ payload = {} }, { call, put }) {
      const { response, data } = yield call(fetchUserSettingsRequest);
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'fetchedSettings',
            payload: data,
          });
        },
        * onError() {

        },
      });
    },
    * fetchSingleAgent({ payload = {} }, { call, put }) {
      const { agentId } = payload;
      const SingleAgentAPIResponse = yield call(fetchUserRequest, {
        params: {
          userId: agentId,
        },
      });
      yield processAPIResponse(SingleAgentAPIResponse, {
        * onSuccess(data) {
          yield put({
            type: 'fetchedAgent',
            payload: data,
          });
        },
      }, 'data');
    },
    * openAgentDetailsDrawer({ payload: { id } }, { call, put }) {
      yield put({
        type: 'toggleDetailsDrawer',
        payload: {
          agentId: id
        }
      })
    },
    * editMyProfileInfo({ payload }, { call, put, select }) {
      const intl = payload?.intl;
      const { response, data } = yield call(editMyProfileRequest, {
          payload: payload.body,
        },
      );
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          message.success(intl.formatMessage({
            id: 'pages.profile.editProfileSuccess',
            defaultMessage: 'Your profile updated successfully',
          }));
          yield put({
            type: 'fetchMe',
          });
        },
        * onError(error) {
          message.error(error?.message || intl.formatMessage({
            id: 'pages.profile.editProfileFailed',
            defaultMessage: 'Edit profile failed',
          }));
        },
      });
    },
    * delete({ payload: { userId, intl } }, { call, put }) {
      const { response, data } = yield call(deleteUserRequest, { params: { userId } });
      yield processAPIResponse({ response, data }, {
        * onSuccess() {
          yield put({
            type: 'getAllUsers',
          });
          intl && message.success(getIntlTranslation(intl, {
            action: INTL_ACTION.REMOVE_SUCCESS,
            entity: INTL_ENTITY.USER,
          }));
        },
        * onError(error) {
          message.error(error?.message || getIntlTranslation(intl, {
            action: INTL_ACTION.REMOVE_FAILED,
            entity: INTL_ENTITY.USER,
          }));

        },
      });
    },

    * getAllUsers({ payload = {} }: { payload: { search?: string, url?: string, page?: number, limit?: number, page_size?: number, with?: string, active?: 'all' | 'only_active' | 'only_inactive', role_id?: string, skills?: string, sort?: string } }, { call, put, select }) {
      const mergedPayload = yield* mergeAndSaveListParams(
        { select, put },
        (state: any) => state.user.listParams,
        'savedListParams',
        payload,
      );

      let query: any = {
        in_scope: 1,
        with: 'availability',
      };
      if (mergedPayload.with)
        query.with = mergedPayload.with;

      if (mergedPayload.search)
        query.search = mergedPayload.search;

      if (mergedPayload.page)
        query.page = mergedPayload.page;

      if (mergedPayload.limit)
        query.page_size = mergedPayload.limit;

      if (mergedPayload.page_size)
        query.page_size = mergedPayload.page_size;

      // 'all' is the API default, but send it explicitly so that switching the
      // filter back to "all" overrides the value merged in from the last fetch.
      if (mergedPayload.active)
        query.active = mergedPayload.active;

      // Comma-separated role hash ids; an empty string clears a previous filter.
      if (mergedPayload.role_id)
        query.role_id = mergedPayload.role_id;

      // Comma-separated skill slugs (any of them); an empty string clears a previous filter.
      if (mergedPayload.skills)
        query.skills = mergedPayload.skills;

      if (mergedPayload.sort) {
        const { sort_by, sort_direction } = parseSort(mergedPayload.sort);
        if (sort_by && sort_direction) {
          query.sort_by = sort_by;
          query.sort_direction = sort_direction;
        }
      }

      const { response, data } = yield call(fetchAllUsersRequest, { query, url: mergedPayload.url });
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'fetchedAllUsers',
            payload: data,
          });
        },
        * onError() {

        },
      });
    },
    * getUserBranches({ payload = {} }, { call, put }) {
      let query = {
        // all: 1,
        in_scope: payload.inScope ? '1' : '0',
      };

      if (payload.search) {
        query.search = payload.search;
      }

      const AllBranchesAPICall = yield call(fetchUserBranches, { query });
      yield processAPIResponse(AllBranchesAPICall, {
        * onSuccess(data) {
          yield put({
            type: payload.inScope ? 'fetchScopedUserBranches' : 'fetchedUserBranches',
            payload: data,
          });
        },
        * onError() {
        },
      });
    },
    * changeSettings({ payload = {} }, { put, call }) {
      const body: any = {};
      if (payload['locale'] !== undefined) body.locale = payload['locale'];
      if (payload['timezone'] !== undefined) body.timezone = payload['timezone'];
      yield call(changeUserSettingsRequest, {
        payload: body,
      });
      if (payload.cb) payload.cb();
      yield put({
        type: 'fetchSettings',
      });
    },
    * resetPassword({ payload = {} }, { put }) {
      yield put({
        type: 'resetPasswordResult',
        payload: {},
      });
    },
    * forgetPassword({ payload = {} }, { put }) {
      yield put({
        type: 'forgetPasswordResult',
        payload: {},
      });
    },
    * fetchConversionalRoles({ payload = {} }, { call, put }) {
      const { response, data } = yield call(fetchConversionalRolesRequest, payload);
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: payload.transferable ? 'fetchedTransferableRoles' : 'fetchedConversionalRoles',
            payload: data.data,
          });
        },
        * onError() {

        },
      });
    },
    * fetchMe({ payload = {} }, { call, put, select }) {
      const { response, data } = yield call(fetchMeRequest);
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'journey/gotJourneys',
            payload: data?.Journeys,
          });
        },
      });
    },
  },
  reducers: {
    gotMe(state, action) {
      return {
        ...state,
        ...action.payload,
        loggedIn: true,
      };
    },
    loggedOut() {
      return initialState;
    },
    reset() {
      return initialState;
    },
    gotUserData(state) {
      return {
        ...state,
      };
    },
    fetchedSettings(state, action) {
      return {
        ...state,
        settings: action.payload,
      };
    },
    fetchedAllUsers(state, action) {
      return {
        ...state,
        list: action.payload,
      };
    },
    savedListParams(state, action) {
      return {
        ...state,
        listParams: action.payload,
      };
    },
    changedSettings(state) {
      return {
        ...state,
      };
    },
    resetPasswordResult(state) {
      return {
        ...state,
      };
    },
    forgetPasswordResult(state) {
      return {
        ...state,
      };
    },
    toggleDrawer(state, action) {
      const drawer = {
        ...(state.drawer || {}),
        ...(action.payload || {}),
        toggledAt: new Date().getTime(),
      };
      drawer.visible = !drawer.visible;
      if (!drawer.visible) {
        delete drawer.onCreate;
      }
      drawer.branch_id = action.payload?.branch_id;
      trackModalView('new-user');
      return {
        ...state,
        drawer,
      };
    },
    toggleDetailsDrawer(state, action) {
      return {
        ...state,
        detailsDrawer: {
          ...(action.payload || {}),
          toggledAt: new Date().getTime(),
        },
      };
    },
    fetchedConversionalRoles(state, action) {
      return {
        ...state,
        conversionalRoles: action.payload,
      };
    },
    fetchedTransferableRoles(state, action) {
      return {
        ...state,
        transferableRoles: action.payload,
      };
    },
    fetchedUserBranches(state, action) {
      return {
        ...state,
        userBranches: action.payload,
      };
    },
    fetchedAgent(state, action) {
      const data = action.payload;
      if (!data?.id) return state

      return {
        ...state,
        agentDetails: {
          ...(state?.agentDetails || {}),
          [data.id]: mergeWith(
            {},
            state?.agentDetails?.[data.id] || {},
            data,
            // Replace instead of deep-merging when either side is an array: a new
            // CalKit agent stores `working_hours: []`, and merging the saved
            // `{ monday: [...] }` into it keeps an empty array with day props,
            // which every reader treats as "no working hours".
            (obj, src) => (Array.isArray(src) || Array.isArray(obj) ? src : undefined),
          ),
        },
      };
    },
    saveUsers(state, action) {
      const users = action.payload || [];
      const agentDetails = { ...(state?.agentDetails || {}) };
      users.forEach((user) => {
        if (user.id) {
          agentDetails[user.id] = {
            ...(agentDetails[user.id] || {}),
            ...user,
          };
        }
      });
      return {
        ...state,
        agentDetails,
      };
    },
    fetchScopedUserBranches(state, action) {
      return {
        ...state,
        scopedUserBranches: action.payload,
      };
    },
    fetchScopedWithStaffUserBranches(state, action) {
      return {
        ...state,
        scopedWithStaffUserBranches: action.payload,
      };
    },
  },
};
